/**
 * TIPOS Y INTERFACES DE DATOS
 * Define estructura de historia laboral y cálculos previsionales
 */

import normativa from './normativa.json'

/**
 * @typedef {Object} PeriodoLaboral
 * @property {string} id - UUID único
 * @property {Date} fechaDesde - Fecha de inicio
 * @property {Date} fechaHasta - Fecha de fin
 * @property {string} tipoActividad - 'dependencia' | 'autonomo' | 'monotributo' | 'docente' | 'insalubre'
 * @property {string} caja - 'AFJP' | 'ANSES' | 'Docente' | 'Insalubre' | 'SFRC'
 * @property {number} aportePorcentaje - % de aporte (ej: 10, 10.5, etc)
 * @property {string} [empresaNombre] - Nombre de la empresa (opcional)
 * @property {string} [cuit] - CUIT de la empresa (opcional)
 */

/**
 * @typedef {Object} HistoriaLaboral
 * @property {string} apellido
 * @property {string} nombre
 * @property {Date} fechaNacimiento
 * @property {string} sexo - 'M' | 'F' | 'X'
 * @property {number} cantidadHijos - Total de hijos vivos
 * @property {number} cantidadHijosAdoptados - Hijos adoptados
 * @property {number} cantidadHijosDiscapacidad - Hijos con discapacidad
 * @property {Array<PeriodoLaboral>} periodos - Bloques de historia laboral
 */

/**
 * @typedef {Object} ResultadoJuridico
 * @property {boolean} cumpleCumpleaños - Cumple edad mínima?
 * @property {number} edadActual - Edad en años
 * @property {boolean} cumpleAportes - Tiene 30 años de aportes?
 * @property {number} aportesTotales - Años + Meses + Días de aportes (en formato decimal)
 * @property {number} apPortesFaltantes - Si es negativo, tiene excedente
 * @property {string} regimen - 'Ordinaria' | 'PUAM' | 'EdadAvanzada' | 'Moratoria' | 'NoAcceso'
 * @property {number} edadRequerida - Edad mínima para el régimen
 * @property {boolean} aplicaCompensacion2x1 - ¿Puede compensar con la regla 2x1?
 * @property {Object} compensacion - {diasExcedentes, diasCompensables, diasFaltantes}
 * @property {Object} tareasDesCuidado - {totalDias, totalAños}
 * @property {Object} cajasPrincipales - Desglose por caja de aportes
 * @property {boolean} requiereReciprocidad - ¿Hay múltiples cajas?
 * @property {string} cajaJubilacion - Caja con más años de aportes
 */

/**
 * @typedef {Object} ResultadoEconomico
 * @property {number} pbu - Prestación Básica Universal (valor fijo)
 * @property {number} promedioRemunerativo - Promedio de 120 meses actualizados
 * @property {number} pc - Prestación Compensatoria (1.5% * años pre-1994)
 * @property {number} pap - Prestación Adicional por Permanencia (1.5% * años post-1994)
 * @property {Array<Object>} desglose - [{concepto, monto, porcentaje}]
 * @property {number} haberBasico - PBU + PC + PAP
 * @property {number} bonos - Bonos vigentes (movilidad, etc)
 * @property {number} haberTotal - Total final estimado
 * @property {Date} fechaCalculoActualizado - Fecha a la que se aplican cálculos
 */

/**
 * @typedef {Object} ResultadoSimulador
 * @property {HistoriaLaboral} historiaLaboral
 * @property {ResultadoJuridico} juridico
 * @property {ResultadoEconomico | null} economico
 * @property {string} estadoGeneral - 'cumple' | 'parcial' | 'nocumple'
 * @property {string} colorSemafo - 'verde' | 'amarillo' | 'rojo'
 * @property {Array<string>} observaciones - Notas importantes
 */

export const TIPOS_ACTIVIDAD = {
  DEPENDENCIA: 'dependencia',
  AUTONOMO: 'autonomo',
  MONOTRIBUTO: 'monotributo',
  DOCENTE: 'docente',
  INSALUBRE: 'insalubre'
}

export const CAJAS = {
  AFJP: 'AFJP',
  ANSES: 'ANSES',
  DOCENTE: 'Docente',
  INSALUBRE: 'Insalubre',
  SFRC: 'SFRC'
}

export const REGIMENES = {
  ORDINARIA: 'Ordinaria',
  PUAM: 'PUAM',
  EDAD_AVANZADA: 'EdadAvanzada',
  MORATORIA: 'Moratoria',
  JUBILACION_ANTICIPADA: 'Jubilación Anticipada',
  NO_ACCESO: 'NoAcceso'
}

export const SEXO = {
  MASCULINO: 'M',
  FEMENINO: 'F',
  OTRO: 'X'
}

/**
 * TOPE MÁXIMO IMPONIBLE (Ley 24.241)
 * Valores históricos para aplicar según período
 * Si la remuneración supera el tope, se usa el tope para el cálculo
 */
export const TOPE_MAXIMO_IMPONIBLE = {
  '2026': 5000000,  // 2026: $5.000.000
  '2025': 4500000,  // 2025: $4.500.000
  '2024': 4000000,  // 2024: $4.000.000
  '2023': 3500000,  // 2023: $3.500.000
  '2022': 3000000,  // 2022: $3.000.000
  '2021': 2500000,  // 2021: $2.500.000
  '2020': 2000000,  // 2020: $2.000.000
  '2019': 1800000,  // 2019: $1.800.000
  '2018': 1500000,  // 2018: $1.500.000
  '2017': 1200000,  // 2017: $1.200.000
  '2016': 1000000,  // 2016: $1.000.000
  '2015': 900000,   // 2015: $900.000
  '2014': 800000,   // 2014: $800.000
  '2013': 700000,   // 2013: $700.000
  '2012': 600000,   // 2012: $600.000
  '2011': 500000,   // 2011: $500.000
  '2010': 450000,   // 2010: $450.000
  '2009': 400000,   // 2009: $400.000
  '2008': 350000,   // 2008: $350.000
  '2007': 300000,   // 2007: $300.000
  '2006': 250000,   // 2006: $250.000
  '2005': 200000,   // 2005: $200.000
  '2004': 180000,   // 2004: $180.000
  '2003': 150000,   // 2003: $150.000
  '2002': 120000,   // 2002: $120.000
  '2001': 100000,   // 2001: $100.000
  '2000': 90000,    // 2000: $90.000
  '1999': 80000,    // 1999: $80.000
  '1998': 70000,    // 1998: $70.000
  '1997': 60000,    // 1997: $60.000
  '1996': 50000,    // 1996: $50.000
  '1995': 45000,    // 1995: $45.000
  '1994': 40000,    // 1994: $40.000
}

/**
 * Obtiene el tope máximo imponible para un año dado
 * Si el año no está en la tabla, devuelve null (sin tope)
 */
export function obtenerTopeImponible(año) {
  const añoStr = año.toString()
  if (TOPE_MAXIMO_IMPONIBLE[añoStr]) {
    return TOPE_MAXIMO_IMPONIBLE[añoStr]
  }
  // Para años anteriores a 1994, usar el tope de 1994
  if (año < 1994) return TOPE_MAXIMO_IMPONIBLE['1994']
  // Para años futuros, usar el último tope conocido
  return TOPE_MAXIMO_IMPONIBLE['2026']
}

// Constantes regulatorias 2026 (DNU 274/2024)
export const EDADES_MINIMAS = Object.freeze({
  Ordinaria: { M: 65, F: 60 },
  PUAM: { M: 65, F: 65 },
  EdadAvanzada: { M: 70, F: 70 },
  JubilacionAnticipada: { M: 60, F: 55 } // Requiere 30 años aportes + desempleo
})

export const ANOS_APORTE_REQUERIDOS = 30

export const FECHA_PIVOTE_APORTE = Object.freeze(new Date('1994-07-01')) // Divide pre/post

export const REGLA_COMPENSACION = Object.freeze({ DIAS_POR_ANO: 0.5 }) // 2 años excedentes = 1 año faltante

export const PORCENTAJE_APORTE = Object.freeze({
  PC: 0.015, // 1.5% por año pre-1994
  PAP: 0.015 // 1.5% por año post-1994
})

// PBU HISTÓRICO - Valores mensuales desde 1994 (Art. 22 Ley 24.241)
// Fuente: ANSES - Resoluciones de movilidad mensual por IPC
// NOTA: Valores con * son ESTIMADOS (proyecciones para simulaciones futuras)
export const PBU_HISTORICO = Object.freeze({
  // Valores base 2024
  "2024-04": 180300,   // Base inicial según Biblia Previsional
  "2024-05": 185000,   // Estimado (ajustar con dato real)
  "2024-06": 192000,   // Estimado
  "2024-07": 198000,   // Estimado
  "2024-08": 205000,   // Estimado
  "2024-09": 212000,   // Estimado
  "2024-10": 220000,   // Estimado
  "2024-11": 235000,   // Estimado
  "2024-12": 250000,   // Estimado
  
  // Valores 2025
  "2025-01": 265000,
  "2025-02": 272000,
  "2025-03": 280000,
  "2025-04": 288000,
  "2025-05": 296000,
  "2025-06": 305000,
  "2025-07": 315000,
  "2025-08": 325000,
  "2025-09": 335000,
  "2025-10": 345000,
  "2025-11": 352000,
  "2025-12": 358000,
  
  // Valores 2026 (Confirmados)
  "2026-01": 355000,
  "2026-02": 363000,
  "2026-03": 368000,
  "2026-04": 370000,
  "2026-05": 372000,   // Confirmado Mayo 2026 (Fuente: ANSES)
  
  // Proyecciones estimadas (marcadas con *)
  "2026-06": 385000,   // * Estimado
  "2026-07": 398000,   // * Estimado
  "2026-08": 412000,   // * Estimado
  "2026-09": 426000,   // * Estimado
  "2026-10": 440000,   // * Estimado
  "2026-11": 455000,   // * Estimado
  "2026-12": 470000,   // * Estimado
})

// PBU según "Biblia Previsional 2026" - Art. 22 Ley 24.241
// Valor ref. Mayo 2026: $372.000,00 (ACTUALIZADO)
// Adicional: 1% por año excedente de 30 hasta año 45
// READONLY CONSTANT para evitar mutaciones en el flujo de reajuste
export const PBU_LEGAL = Object.freeze({ 
  valor: Number(normativa?.topes?.pbuVigente || 372000), // Dinámico desde normativa.json
  actualizar: (nuevoValor) => {
    console.log(`⚠️ PBU_LEGAL.valor es readonly. Use PBU_VIGENTE dinámico si necesita cambios.`)
  }
})

// PBU Vigente (dinámico para Worker) - Mayo 2026
export let PBU_VIGENTE = Number(normativa?.topes?.pbuVigente || 372000)

// Función para obtener PBU en una fecha específica (para liquidaciones retroactivas)
// Usa "Fecha de Adquisición de Derecho" del cliente
export function obtenerPBUEnFecha(fechaAdquisicionDerecho) {
  const fechasOrdenadas = Object.keys(PBU_HISTORICO).sort()
  const primeraFecha = fechasOrdenadas[0]
  const ultimaFecha = fechasOrdenadas[fechasOrdenadas.length - 1]
  const fallback = PBU_HISTORICO[ultimaFecha] || PBU_VIGENTE

  if (!(fechaAdquisicionDerecho instanceof Date) || Number.isNaN(fechaAdquisicionDerecho.getTime())) {
    return fallback
  }

  const periodo = fechaAdquisicionDerecho.toISOString().slice(0, 7) // "YYYY-MM"

  // Buscar exacto en histórico
  if (PBU_HISTORICO[periodo]) return PBU_HISTORICO[periodo]

  // Si es anterior al primer dato, usar el primer valor disponible sin warning
  if (periodo < primeraFecha) {
    return PBU_HISTORICO[primeraFecha] || fallback
  }

  // Si es posterior o intermedio sin dato puntual, usar el último conocido
  console.warn(`⚠️ PBU para ${periodo} no encontrado. Usando último: ${ultimaFecha} = $${PBU_HISTORICO[ultimaFecha]}`)
  return fallback
}

// Función para actualizar PBU dinámicamente (llamada por Worker)
export function actualizarPBU(periodo, nuevoValor) {
  // Actualizar en histórico (si es mes real)
  if (PBU_HISTORICO[periodo] && !periodo.includes('*')) {
    console.log(`⚠️ Sobrescribiendo PBU real ${periodo}: $${nuevoValor}`)
  }
  // Actualizar vigente
  PBU_VIGENTE = nuevoValor
  console.log(`PBU vigente actualizado a: $${nuevoValor} (${periodo})`)
}

// Haber Mínimo Ley 24.241 (Mayo 2026: $383.000,00)
// Según "Biblia Previsional": "El haber mínimo es un piso legal, NO un componente de cálculo"
// READONLY CONSTANT para evitar mutaciones - EXACTAMENTE 383000.00
export const HABER_MINIMO_LEGAL = Object.freeze(Number(normativa?.topes?.haberMinimoLegal || 383000.00))

export const TAREAS_CUIDADO = Object.freeze({
  POR_HIJO: 1, // 1 año de aporte
  POR_HIJO_ADOPTADO: 2, // 2 años
  POR_HIJO_DISCAPACIDAD: 2, // 2 años
  POR_HIJO_BIOLOGICO: 1, // 1 año adicional por hijo biológico
  POR_HIJO_ADOPTADO_EXTRA: 2, // 2 años por adoptado
  POR_HIJO_DISCAPACIDAD_EXTRA: 2, // 2 años por discapacidad
  PLUS_AUH: 1 // 1 año adicional si cobra AUH
})

// CAJAS Y REGÍMENES ESPECIALES
export const CAJAS_SOPORTADAS = {
  ANSES: 'ANSES',
  'IPS-BA': 'IPS-BA',
  DOCENTE: 'DOCENTE',
  INSALUBRE: 'INSALUBRE',
  SFRC: 'SFRC'
}

export const REGIMENES_ESPECIALES = {
  DOCENTE: 'DOCENTE',
  INSALUBRE: 'INSALUBRE',
  EDAD_AVANZADA: 'EDAD_AVANZADA'
}

// PROFESIONES QUE CALIFICAN COMO INSALUBRES
export const PROFESIONES_INSALUBRES = [
  'POLICIA',
  'BOMBERO',
  'PENITENCIARIO',
  'MINERO',
  'QUIMICO',
  'SANITARIO',
  'AGRICULTOR'
]

// MEJOR CARGO - Escalas de cálculo
export const ESCALA_PORCENTAJE_IPS = {
  25: 70,
  30: 75,
  35: 82
}

// Base de datos de Regímenes Especiales (prorrateo)
export const REGIMENES_DB = Object.freeze({
  CONSTRUCCION: { edad: 55, aportes: 25, descripcion: 'Régimen de Construcción' },
  CHOFERES: { edad: 55, aportes: 30, descripcion: 'Régimen de Choferes' },
  DOCENTES: { edad: 60, aportes: 25, descripcion: 'Régimen Docente' },
  SALUD: { edad: 60, aportes: 30, descripcion: 'Régimen de Salud' },
  GENERAL: Object.freeze({
    M: { edad: 65, aportes: 30 },
    F: { edad: 60, aportes: 30 }
  })
})

// Configuración de Tareas de Cuidado AMPLIADA
export const TAREAS_CUIDADO_AMPLIADO = Object.freeze({
  POR_HIJO_BIOLOGICO: 1,
  POR_HIJO_ADOPTADO: 2,
  POR_HIJO_ADOPTADO_EXTRA: 2,
  POR_HIJO_DISCAPACIDAD: 2,
  PLUS_AUH: 1
})

// PREVISIONAL_CONSTANTS - Agrupación de constantes previsionales (Object.freeze)
// HABER_MINIMO = 383000.00 exacto, PBU_LEGAL = 180300.00 exacto
export const PREVISIONAL_CONSTANTS = Object.freeze({
  PBU_LEGAL,
  HABER_MINIMO: HABER_MINIMO_LEGAL, // Exactamente 383000.00
  EDADES_MINIMAS,
  ANOS_APORTE_REQUERIDOS,
  FECHA_PIVOTE_APORTE,
  REGLA_COMPENSACION,
  PORCENTAJE_APORTE,
  TAREAS_CUIDADO,
  TAREAS_CUIDADO_AMPLIADO,
  REGIMENES_DB
})

// Alias HABER_MINIMO (383000.00 exacto) para cumplir con la interfaz solicitada
export const HABER_MINIMO = HABER_MINIMO_LEGAL

// Actualizado según movilidad mayo 2026 - Ley 27.705
// Base Imponible Mínima Mayo 2026 según topes-historicos
export const BASE_IMPONIBLE_MINIMA = Number(normativa?.topes?.baseImponibleMinima || 450000)

// UPDP (Unidad de Pago de Deuda Previsional) - Ley 27.705
// Fórmula: 29% de la Base Imponible Mínima vigente
// Actualizado según movilidad mayo 2026 - Ley 27.705
export const VALOR_UPDP_ACTUAL = +(BASE_IMPONIBLE_MINIMA * 0.29).toFixed(2)  // 130500.00

// Normativa dinámica consumida por el motor (base para Oracle)
export const NORMATIVA_VIGENTE = Object.freeze({
  version: normativa?.version || 'n/a',
  actualizadoEn: normativa?.actualizadoEn || null,
  fuente: normativa?.fuente || 'no informada',
  movilidadVigente: normativa?.movilidadVigente || null,
  fallosActivos: Array.isArray(normativa?.fallosActivos) ? normativa.fallosActivos : []
})
